import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  ReportCitations,
  ReportFeatureCards,
  ReportFeedRow,
  ReportKpi,
  ReportMapPack,
  ReportSearch,
  ReportUpcoming,
  ReportWorkRow,
} from '../types/report.types';
import { CitationHealthSection, FoundationEyebrow } from './CitationHealth';
import { EmptyNote, RankGrid } from './MoreSections';
import { ScanGridSection, type ScanMapUrl } from './ScanGrid';
import { SearchPerformanceSection } from './SearchPerformance';
import { CountsRow, FeatureCards, Feed, UpcomingWork } from './WorkCards';
import {
  BODY_FG,
  CARD_BORDER,
  DEFAULT_BRAND,
  FAINT_FG,
  FONT,
  HEADING_FG,
  KPI_REASON_FG,
  MUTED_FG,
  POSITIVE_BG,
  POSITIVE_FG,
  WORK_RULE_SOFT,
  cardSx,
  eyebrowSx,
  sectionSubtitleSx,
  sectionTitleSx,
  tableSx,
} from '../reportTokens';
import { percentText } from '../reportPercent';

/**
 * Fixed copy, verbatim from the design's `workWhy` — the standing reason the
 * section exists, not derived from the payload, so it reads the same every
 * week including a v2 snapshot with nothing else in this section.
 */
const WORK_WHY =
  'Google favors profiles that stay active. Every post, reply, photo, and service description is fresh content it can index, and it is the same content AI search tools read when they answer questions about local businesses. Consistent weekly activity is what keeps you visible in both.';

const formatNumber = (value: number) => value.toLocaleString();

/**
 * Whether the card's figure fell or held against the period before. Its reason
 * line is then not drawn: no longer written for one, but a hosted report sent
 * before that keeps "N fewer than last week" in its snapshot. Read from the
 * figures, not the sentence, so a reworded or translated reason still drops.
 */
const noGain = (kpi: ReportKpi) =>
  // Absent on snapshots built before the flag: `previous > 0`, as
  // `buildKpiReason` reads it, so a stored "First week with data" stays.
  (kpi.hasPrevious ?? kpi.previous > 0)
    ? kpi.value <= kpi.previous
    : // The legacy GBP path (`windowDays`) never had a prior period, and its
      // stored "First week with data" claimed a first week every week.
      kpi.windowDays !== undefined;

/**
 * A rise, or nothing. The client report shows improvements only, so a fall,
 * a flat week, or no basis to compare leaves the figure without a chip.
 */
function TrendChip({ changePct }: { changePct: number | null }) {
  if (changePct === null || changePct < 0.05) return null;
  return (
    <Box
      component='span'
      sx={{
        // Exact design values. The report is a standalone client-facing
        // document, not a screen inside the app, so it follows the design's
        // own scale rather than the app's rem tokens.
        fontSize: '11px',
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: '9999px',
        color: POSITIVE_FG,
        bgcolor: POSITIVE_BG,
      }}
    >
      {`↑ ${changePct.toFixed(1)}%`}
    </Box>
  );
}

export function Section({
  title,
  eyebrow,
  subtitle,
  meta,
  subtitleGap = '14px',
  children,
}: {
  title: string;
  /** A tag drawn above the title — the design's "Foundation" tier on
   * Citation Health. */
  eyebrow?: React.ReactNode;
  subtitle?: string;
  /** A short fact on the title's own line, right-aligned — the design uses
   * it for scan configuration and sync status. Omitted when there is none,
   * which leaves the title full width rather than a dangling gap. */
  meta?: string;
  /** How far the heading block sits above the body. The design is not
   * uniform here: 14px under a subtitle that introduces the very next line,
   * 24px under one that introduces a whole block. */
  subtitleGap?: string;
  children: React.ReactNode;
}) {
  return (
    <Box className='report-section' sx={cardSx}>
      {eyebrow && <Box sx={{ mb: '10px' }}>{eyebrow}</Box>}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: '4px',
        }}
      >
        <Typography component='h2' sx={sectionTitleSx}>
          {title}
        </Typography>
        {meta && (
          <Typography sx={{ fontSize: '12px', color: MUTED_FG }}>
            {meta}
          </Typography>
        )}
      </Box>
      {/* The subtitle carries the gap to whatever follows — the design spaces
          the heading block by margins on its own lines rather than by padding
          on a wrapper. */}
      {subtitle && (
        <Typography sx={{ fontSize: '14px', color: MUTED_FG, mb: subtitleGap }}>
          {subtitle}
        </Typography>
      )}
      <Box sx={subtitle ? undefined : { mt: subtitleGap }}>{children}</Box>
    </Box>
  );
}

/**
 * The four headline numbers, week against the week before.
 *
 * No trend line: the client report shows improvements only, and a line over
 * the weeks draws every dip, even on a week that ended up. The weekly series
 * stays in the snapshot.
 */
export function KpiGrid({ kpis }: { kpis: ReportKpi[] }) {
  return (
    <Box
      sx={{
        display: 'grid',
        // The design's own grid: cards wrap below 200px rather than squeezing
        // a 34px figure into a column too narrow to hold it.
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '14px',
      }}
    >
      {kpis.map(kpi => {
        // The card leads with the all-time total and puts the week under it.
        // Without an all-time aggregate there is nothing to lead with, so the
        // week's own number takes the headline rather than printing a 0 that
        // would read as "no calls ever".
        const sinceJoining =
          kpi.cumulative !== null && kpi.cumulative !== undefined;
        const headline = sinceJoining ? (kpi.cumulative as number) : kpi.value;
        return (
          <Box
            key={kpi.key}
            data-testid='kpi-card'
            sx={{
              // Each KPI is its own card. They used to share one, which is the
              // single biggest reason this row did not read like the design:
              // four figures inside one frame scan as a table, not as cards.
              background: '#fff',
              border: '1px solid',
              borderColor: CARD_BORDER,
              borderRadius: '16px',
              p: '18px',
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: '10px',
              }}
            >
              <Typography
                component='span'
                sx={{ fontSize: '13px', fontWeight: 600, color: BODY_FG }}
              >
                {kpi.label}
              </Typography>
              {/* Top-right, beside the label — not beside the figure. */}
              <TrendChip changePct={kpi.changePct} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '7px' }}>
              <Typography
                component='span'
                sx={{
                  fontSize: '34px',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05,
                  color: HEADING_FG,
                }}
              >
                {formatNumber(headline)}
              </Typography>
              {sinceJoining && (
                <Typography
                  component='span'
                  sx={{ fontSize: '12px', color: MUTED_FG }}
                >
                  since joining
                </Typography>
              )}
            </Box>

            <Typography sx={{ fontSize: '12px', color: MUTED_FG, mt: '4px' }}>
              {/* "this week" only when the figure IS the week. The legacy GBP
                  path floors its window at a fortnight, so calling its totals
                  weekly overstated every headline number by roughly double —
                  the email it comes from has always said "(14 days)" in its
                  own heading. */}
              {formatNumber(kpi.value)}{' '}
              {kpi.windowDays
                ? `in the last ${kpi.windowDays} days`
                : 'this week'}
              {/* No last-week figure: the client report shows improvements
                  only, and "100 · 120 last week" is a drop told in numbers.
                  A gain is said by the reason line below, as the legacy email
                  never printed last week's figure either. */}
            </Typography>

            {/* Amber, per the design: the line saying WHY a number moved is
                where the eye should land after the figure, and grey sinks it
                into the two neutral lines around it. Absent on a v2 snapshot,
                which predates this narrative line. */}
            {kpi.reason && !noGain(kpi) && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: KPI_REASON_FG,
                  lineHeight: 1.5,
                  mt: '4px',
                }}
              >
                {kpi.reason}
              </Typography>
            )}

            {kpi.fromSearch !== undefined && kpi.fromMaps !== undefined && (
              <Typography sx={{ fontSize: '12px', color: FAINT_FG, mt: '2px' }}>
                {formatNumber(kpi.fromSearch)} from Search ·{' '}
                {formatNumber(kpi.fromMaps)} from Maps
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * What was actually done this week: the feature cards for one GBP post, one
 * review reply and one listed service, the counts tiles, the full dated
 * feed and What's next — the design's "Work Published This Week" in full.
 *
 * `cards`, `feed` and `upcoming` are all optional because a v2 snapshot
 * predates them; each of the four blocks below renders nothing on its own
 * when its data is empty, so a quiet week (or an old snapshot) collapses to
 * just the subtitle and the standing explainer rather than four empty
 * frames. The section itself still always renders — see `Section` above.
 */
export function WorkSection({
  rows,
  total,
  cards,
  feed,
  upcoming,
}: {
  rows: ReportWorkRow[];
  /** The week's whole count, published pages included; the tiles' sum when
   * an older snapshot has none. */
  total?: number;
  cards?: ReportFeatureCards;
  feed?: ReportFeedRow[];
  upcoming?: ReportUpcoming[];
}) {
  const workTotal = total ?? rows.reduce((sum, row) => sum + row.count, 0);
  const workSub = `${workTotal} change${workTotal === 1 ? '' : 's'} happened this week.`;

  return (
    <Section title='Work Published This Week' subtitle={workSub}>
      <Typography
        sx={{
          fontSize: '14px',
          // Body grey, not the muted grey. This paragraph is read, not
          // skimmed past, and the design gives it the same weight as prose.
          color: BODY_FG,
          lineHeight: 1.7,
          borderLeft: `3px solid ${WORK_RULE_SOFT}`,
          padding: '2px 0 2px 16px',
          mb: '24px',
        }}
      >
        {WORK_WHY}
      </Typography>
      {/* No wrapper gap: in the design each block below carries its own
          rule and its own 20px above it, so a uniform flex gap would add a
          second, competing rhythm — and would space a block that rendered
          nothing. */}
      <FeatureCards cards={cards} />
      <CountsRow rows={rows} />
      <Feed rows={feed ?? []} />
      <UpcomingWork items={upcoming ?? []} />
    </Section>
  );
}

/**
 * "Scanned weekly across 25 search points · 5 mi radius".
 *
 * Every clause is dropped rather than defaulted when its figure is missing,
 * so a v2 snapshot (no `scanArea` at all) shows no line instead of a
 * sentence full of placeholders, and a grid size with no span shows the
 * point count alone.
 */
function scanAreaLine(area: ReportMapPack['scanArea']): string | undefined {
  if (!area) return undefined;
  const parts: string[] = [];
  const cadence = area.frequency ? `Scanned ${area.frequency}` : 'Scanned';
  parts.push(
    `${cadence} across ${area.points} search point${area.points === 1 ? '' : 's'}`,
  );
  if (area.radius) {
    // The ladder is defined in miles; `buildScanArea` has already converted
    // to the company's own unit, so this only shortens the word.
    parts.push(
      `${area.radius} ${area.unit === 'kilometers' ? 'km' : 'mi'} radius`,
    );
  }
  return parts.join(' · ');
}

export function MapPackSection({
  mapPack,
  agencyView = false,
  mapUrl,
}: {
  mapPack: ReportMapPack;
  /** Passed through to the scan footnote, which is agency-only. */
  agencyView?: boolean;
  /** Passed through to the scan grid, for its street maps. */
  mapUrl?: ScanMapUrl;
}) {
  // The design's baseline-vs-now scan grid needs `scanGrid` rows, which a v2
  // snapshot never sends. When it's missing (or empty), fall back to the
  // older single-number Visibility figure and four-column table rather than
  // rendering `ScanGridSection` with nothing to draw.
  const scanGrid = mapPack.scanGrid;
  const hasScanGrid = !!scanGrid && scanGrid.length > 0;

  return (
    <Section
      title='Map Pack Tracking'
      meta={scanAreaLine(mapPack.scanArea)}
      subtitle='Average position in the local map pack for each tracked keyword, compared to where you started.'
      subtitleGap='24px'
    >
      {!mapPack.enabled && (
        <EmptyNote>
          {mapPack.loadError
            ? 'Your rankings could not be loaded for this week. Nothing has been lost — they will be back in the next report.'
            : mapPack.trackingOn
              ? 'No scan ran during this week, so there are no new positions to report.'
              : 'Map tracking is not switched on for this business yet.'}
        </EmptyNote>
      )}
      {mapPack.enabled && (
        <>
          {hasScanGrid ? (
            <ScanGridSection
              keywords={scanGrid as NonNullable<typeof scanGrid>}
              baselineDate={mapPack.scanGridBaselineDate}
              scanArea={mapPack.scanArea}
              agencyView={agencyView}
              mapUrl={mapUrl}
            />
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography sx={eyebrowSx}>Visibility</Typography>
                <Typography
                  sx={{ fontSize: FONT.hero, fontWeight: 800, lineHeight: 1.2 }}
                >
                  {percentText(mapPack.visibilityRate)}%
                </Typography>
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ ...tableSx, minWidth: 420 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Keyword</TableCell>
                      <TableCell align='right'>Avg rank</TableCell>
                      <TableCell align='right'>Top 3</TableCell>
                      <TableCell align='right'>Top 10</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mapPack.keywords.map(row => (
                      <TableRow key={row.keyword}>
                        <TableCell sx={{ fontWeight: 600, color: HEADING_FG }}>
                          {row.keyword}
                        </TableCell>
                        <TableCell align='right'>
                          {/* Not ranked anywhere on the grid is a real result, and an
                          em dash says it without implying a position. */}
                          {row.averageRanking === null
                            ? '—'
                            : row.averageRanking.toFixed(1)}
                        </TableCell>
                        <TableCell align='right'>{row.top3}</TableCell>
                        <TableCell align='right'>{row.top10}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          <RankGrid history={mapPack.history} />
        </>
      )}
    </Section>
  );
}

/** "Sep 6, 2026", or '' when the date is missing or unreadable. */
function syncedOn(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
}

/**
 * The full design (donut, band pill, stat blocks, Citation Network) lives in
 * `CitationHealthSection` (CitationHealth.tsx) — this wrapper's only job is
 * the section chrome and the one empty state a missing citations
 * subscription needs, same as it always has.
 */
export function CitationsSection({
  citations,
}: {
  citations: ReportCitations;
}) {
  const total = citations.totalListings;
  // The design's copy, minus its "re-check them every 24 hours": the sync
  // runs when a profile change is detected (`CitationAutoSync`), not on a
  // daily timer, and a client report should not promise a cadence we do not
  // keep.
  const subtitle =
    citations.enabled && total
      ? `Your name, address and phone number across the directories Google and AI search trust most. We publish your details to ${total} ${total === 1 ? 'directory' : 'directories'} and correct anything that drifts.`
      : 'Your business details across the directories people search.';
  const synced = syncedOn(citations.lastSyncedAt);
  const meta =
    citations.enabled && total
      ? `Scanned ${total} ${total === 1 ? 'directory' : 'directories'}${synced ? ` · ${synced}` : ''}`
      : undefined;
  return (
    <Section
      title='Citation Health'
      eyebrow={citations.enabled ? <FoundationEyebrow /> : undefined}
      meta={meta}
      subtitle={subtitle}
    >
      {!citations.enabled && (
        <EmptyNote>Citation management is not part of this plan yet.</EmptyNote>
      )}
      {citations.enabled && <CitationHealthSection citations={citations} />}
    </Section>
  );
}

/**
 * The full design (four stat tiles, Pages on Page 1, Queries Ranking) lives
 * in `SearchPerformanceSection` (SearchPerformance.tsx) — this wrapper's only
 * job is the section chrome and the one empty state a missing Search Console
 * connection needs, same as it always has.
 */
export function SearchSection({
  search,
  preview = false,
}: {
  search: ReportSearch;
  /** The agency's preview, which also gets the notes the client does not. */
  preview?: boolean;
}) {
  const splitHosts = search.splitHosts || [];
  return (
    <Section
      title='Website Search Performance'
      meta={search.enabled ? 'Google Search Console · last 7 days' : undefined}
      subtitle='How your website performed in Google search results this week.'
      subtitleGap='22px'
    >
      {/* The page table merges a page Google listed under both www and the
          bare domain, which is right for the client and hides the cause from
          everyone. The agency is the one who can fix it, so its preview says
          so. */}
      {preview && search.enabled && splitHosts.length > 1 && (
        <Typography
          role='note'
          className='report-no-print'
          sx={{ ...sectionSubtitleSx, mb: 2, fontWeight: 600 }}
        >
          Only you see this: Google lists this site under both{' '}
          {splitHosts.join(' and ')}, so its rankings are split between them.
          Pages are combined below. Make the site&apos;s canonical tag and
          redirect point to the same address and Google will settle on one.
        </Typography>
      )}
      {!search.enabled && (
        <EmptyNote>
          Search Console is not connected for this website yet.
        </EmptyNote>
      )}
      {search.enabled && <SearchPerformanceSection search={search} />}
    </Section>
  );
}

export { DEFAULT_BRAND };
